'use client';

import { useEffect, useRef } from 'react';
import { ResultsPerPageControl } from '../../ResultsPerPage/ResultsPerPageControl';
import { isPageSizeOption } from '../../utils/pageSize';
import {
  getResultsPerPageFromLocalStorage,
  syncResultsPerPageCookie,
} from '../../utils/resultsPerPageStorage';
import { registerNumberOfResultsAsDefault } from '../headless/pagination';
import { sanitizeSearchParams } from '../headless/searchParams';
import { useSearchV2ResultsPerPage } from '../hooks/useSearchV2ResultsPerPage';
import { ResultsPerPageProps } from './ResultsPerPage.types';

/**
 * Whether the current URL already specifies a page size the engine will
 * actually honor. On the SSR path state lives in the query string, but legacy
 * hash deep links still carry it in the fragment until the one-shot migration
 * rewrites them — honor both. Runs through the same sanitizer as the server
 * seeding, so a value it discards (e.g. numberOfResults=0) doesn't block the
 * saved preference from applying.
 */
const urlSpecifiesNumberOfResults = () => {
  const url = new URL(window.location.href);
  return (
    sanitizeSearchParams(url.searchParams).has('numberOfResults') ||
    sanitizeSearchParams(new URLSearchParams(url.hash.slice(1))).has(
      'numberOfResults'
    )
  );
};

export const ResultsPerPage = ({ className }: ResultsPerPageProps) => {
  const { state, methods, engine, selectResultsPerPage } =
    useSearchV2ResultsPerPage();

  // Reconcile the saved page-size preference once per hydrated engine, unless
  // the URL specifies numberOfResults (the URL wins — e.g. shared/legacy
  // links). The preference normally reaches the server through its cookie
  // mirror (SearchSsrPage seeds the first query at it), so for most users the
  // state already matches and nothing re-queries here. This effect is the
  // migration/self-heal path for localStorage-only preferences (saved before
  // the cookie existed, or a cleared/stale cookie): it re-syncs the cookie so
  // the NEXT server render is right, and applies the preference (one re-query)
  // when the server rendered at a different size. The applied preference is also made the pagination
  // default so the SearchParameterManager keeps it out of serialized URLs.
  //
  // Keyed on the `methods` identity, not a boolean: a re-hydration (e.g. a
  // currency change streaming a new staticState) delivers a REPLACEMENT engine
  // seeded with the flag default and new controller methods, without
  // remounting this subtree — the preference has to be applied to it again.
  // While the URL specifies numberOfResults the guard stays UNSET, so the
  // preference still applies once a history navigation reaches an entry
  // without the parameter and the URL stops overriding it.
  const appliedPrefFor = useRef<typeof methods>();
  useEffect(() => {
    if (!methods || appliedPrefFor.current === methods) return;
    if (urlSpecifiesNumberOfResults()) return;
    appliedPrefFor.current = methods;
    const pref = getResultsPerPageFromLocalStorage();
    if (pref !== undefined && isPageSizeOption(pref)) {
      syncResultsPerPageCookie(pref);
      if (pref !== state.numberOfResults) {
        methods.set(pref);
        registerNumberOfResultsAsDefault(engine, pref);
      }
    }
  }, [methods, engine, state.numberOfResults]);

  return (
    <ResultsPerPageControl
      numberOfResults={state.numberOfResults}
      onSelect={selectResultsPerPage}
      className={className}
      disabled={!methods}
    />
  );
};
